import { StudioInputError } from './errors.js';
import { z } from '../../renderer/src/schema-library.js';
import { CAMERA_TRACKS, CAMERA_TRACK_LIMITS, emptyCameraKeyframes, resolveCameraKeyframes, type CameraTrack } from '../../renderer/src/camera-keyframes.js';
import { CinematicWaypointSchema, CinematicSequenceSchema } from '../../renderer/src/cinematic-sequence-contract.js';
import { cinematicTimeline } from '../../renderer/src/../../web/src/domain/cinematic-timeline';
import { upsertAttributeKey, editableCameraTracks } from '../../renderer/src/../../web/src/studio/camera-tracks/camera-animation';
import { upsertSceneKey } from '../../renderer/src/../../web/src/studio/scene-keyframes';
import { lensForCameraDistance } from '../../renderer/src/camera-lens.js';
import type { RidePoint } from '../../renderer/src/../../web/src/domain/ride';
import { StudioSettingsSchema, type StudioSettings } from './settings.js';
import { StudioPositionSchema, resolveStudioPosition, type StudioPosition } from './positions.js';
import { DepthOfFieldSchema } from '../../renderer/src/depth-of-field-contract.js';
import { evaluateStudioAt } from './evaluate.js';

const numeric = Object.fromEntries(CAMERA_TRACKS.map(axis => [axis, z.number().min(CAMERA_TRACK_LIMITS[axis].min).max(CAMERA_TRACK_LIMITS[axis].max).optional()])) as Record<CameraTrack, z.ZodOptional<z.ZodNumber>>;
export const StudioKeyValuesSchema = z.object({ ...numeric, ...CinematicWaypointSchema.shape.overrides.shape,
  depthOfField: DepthOfFieldSchema.partial().optional(), automatic: z.array(z.enum(CAMERA_TRACKS)).min(1).max(5).optional() }).strict();
export const StudioIncomingSchema = z.object({ type: z.enum(['cut', 'transition']).default('transition'),
  durationSeconds: z.union([z.number().min(.1).max(30), z.literal('interval')]).default(2), easing: z.enum(['linear', 'smooth']).default('smooth') }).strict();
export const StudioDocumentSchema = z.object({ version: z.literal(1), title: z.string().trim().min(1).max(120),
  settings: StudioSettingsSchema, positions: z.record(z.string(), StudioPositionSchema).default({}) }).strict();
export type StudioDocument = z.infer<typeof StudioDocumentSchema>;

export function listStudioKeys(points: readonly RidePoint[], settings: StudioSettings) {
  const sequence = settings.cinematicSequence ?? { version: 1 as const, waypoints: [] };
  return [
    ...cinematicTimeline(points, sequence, settings.durationSeconds).map(item => ({ id: item.waypoint.id, kind: 'scene',
      frame: Math.round(item.seconds / settings.durationSeconds * (settings.durationSeconds * 30 - 1)), compositionSeconds: item.seconds,
      values: item.waypoint.overrides, incoming: item.waypoint.transition })),
    ...CAMERA_TRACKS.flatMap(axis => (sequence.cameraTracks?.[axis] ?? []).map(key => ({ id: key.id, kind: axis,
      frame: Math.round(key.seconds / settings.durationSeconds * (settings.durationSeconds * 30 - 1)), compositionSeconds: key.seconds,
      values: { [axis]: key.automatic ? 'automatic' : key.value }, incoming: { type: key.transition ?? 'transition', durationSeconds: key.durationSeconds ?? 2, easing: key.easing } }))),
  ].sort((a, b) => a.frame - b.frame || a.kind.localeCompare(b.kind));
}

/** Sparse changes preserve every unrelated property and neighboring destination. */
export function upsertStudioKey(document: StudioDocument, points: readonly RidePoint[], position: unknown, values: unknown, incoming?: unknown) {
  const next = structuredClone(document), data = StudioKeyValuesSchema.parse(values), timing = StudioIncomingSchema.parse(incoming ?? {});
  if (!Object.keys(data).length) throw new StudioInputError('Supply at least one keyframe property.');
  const resolved = resolveStudioPosition(points, next.settings, position);
  const sequence = next.settings.cinematicSequence ?? { version: 1 as const, waypoints: [] };
  const effective = data.depthOfField || data.presentationAppearance ? evaluateStudioAt(points, next.settings, position).settings : undefined;
  const changes = { ...data, ...(data.depthOfField ? { depthOfField: { ...effective!.depthOfField, ...data.depthOfField } } : {}),
    ...(data.presentationAppearance ? { presentationAppearance: { ...effective!.presentationAppearance, ...data.presentationAppearance } } : {}) };
  const overrides = CinematicWaypointSchema.shape.overrides.parse(Object.fromEntries(Object.entries(changes).filter(([key]) => !CAMERA_TRACKS.includes(key as CameraTrack) && key !== 'automatic')));
  const ids: string[] = [];
  if (Object.keys(overrides).length) {
    const transition = timing.type === 'cut' ? { type: 'cut' as const, timing: 'arrival' as const }
      : { type: 'smooth' as const, durationSeconds: timing.durationSeconds === 'interval' ? 2 : timing.durationSeconds,
        entireInterval: timing.durationSeconds === 'interval', easing: timing.easing, timing: 'arrival' as const };
    const edited = upsertSceneKey(points, sequence, resolved.filmProgress, next.settings.durationSeconds, crypto.randomUUID(), overrides,
      incoming === undefined ? undefined : transition);
    sequence.waypoints = edited.sequence.waypoints.map(key => edited.keyIds.includes(key.id) ? { ...key, anchor: resolved.anchor } : key);
    for (const id of edited.keyIds) { next.positions[id] = resolved.requested; ids.push(id); }
  }
  for (const axis of CAMERA_TRACKS) {
    const automatic = data.automatic?.includes(axis) ?? false;
    if (data[axis] === undefined && !automatic) continue;
    const baseline = axis === 'lens' ? lensForCameraDistance(next.settings.cameraDistance) : axis === 'altitude' ? 200 : 0;
    const hadKeys = !!sequence.cameraTracks?.[axis]?.length;
    const edited = upsertAttributeKey(sequence.cameraTracks, axis, resolved.compositionSeconds, data[axis] ?? baseline, baseline, next.settings.durationSeconds, automatic);
    if (!hadKeys && edited.key.seconds > .001) edited.tracks[axis] = edited.tracks[axis]!.map(item => item.id === edited.key.id ? item : { ...item, automatic: true });
    const key = incoming === undefined ? edited.key : { ...edited.key, transition: timing.type, durationSeconds: timing.durationSeconds, easing: timing.easing };
    sequence.cameraTracks = { ...edited.tracks, [axis]: edited.tracks[axis]!.map(item => item.id === key.id ? key : item) };
    next.positions[key.id!] = resolved.requested; ids.push(key.id!);
  }
  next.settings.cinematicSequence = CinematicSequenceSchema.parse(sequence);
  next.settings.cinematicSequence.waypoints = cinematicTimeline(points, next.settings.cinematicSequence, next.settings.durationSeconds).map(item => item.waypoint);
  StudioSettingsSchema.parse(next.settings);
  return { document: next, resolved, keyIds: ids };
}

export function deleteStudioKey(document: StudioDocument, keyId: string) {
  const next = structuredClone(document), sequence = next.settings.cinematicSequence;
  if (!sequence) throw new StudioInputError('Keyframe not found.');
  let found = sequence.waypoints.some(key => key.id === keyId);
  sequence.waypoints = sequence.waypoints.filter(key => key.id !== keyId);
  if (sequence.cameraTracks) {
    const tracks = editableCameraTracks(sequence.cameraTracks);
    sequence.cameraTracks = tracks;
    for (const axis of CAMERA_TRACKS) {
      found ||= tracks[axis]!.some(key => key.id === keyId);
      tracks[axis] = tracks[axis]!.filter(key => key.id !== keyId);
    }
  }
  if (!found) throw new StudioInputError('Keyframe not found.');
  delete next.positions[keyId];
  return StudioDocumentSchema.parse(next);
}

export function moveStudioKey(document: StudioDocument, points: readonly RidePoint[], keyId: string, position: unknown) {
  const next = structuredClone(document), sequence = next.settings.cinematicSequence;
  const resolved = resolveStudioPosition(points, next.settings, position);
  if (!sequence) throw new StudioInputError('Keyframe not found.');
  const listed = listStudioKeys(points, next.settings), key = listed.find(item => item.id === keyId);
  if (!key) throw new StudioInputError('Keyframe not found.');
  if (listed.some(item => item.id !== keyId && item.kind === key.kind && item.frame === resolved.frame)) throw new StudioInputError('Another key for this property occupies the destination frame.');
  const scene = sequence.waypoints.find(item => item.id === keyId);
  if (scene) { scene.filmProgress = resolved.filmProgress; scene.anchor = resolved.anchor; }
  else for (const axis of CAMERA_TRACKS) for (const item of sequence.cameraTracks?.[axis] ?? []) if (item.id === keyId) item.seconds = resolved.compositionSeconds;
  next.positions[keyId] = resolved.requested;
  if (sequence.cameraTracks) sequence.cameraTracks = resolveCameraKeyframes(sequence.cameraTracks, next.settings.durationSeconds);
  cinematicTimeline(points, sequence, next.settings.durationSeconds);
  return StudioDocumentSchema.parse(next);
}

/** Retiming preserves the authored position basis; legacy keys retain relative timing. */
export function updateStudioSettings(document: StudioDocument, points: readonly RidePoint[], patch: unknown) {
  const next = structuredClone(document), oldDuration = next.settings.durationSeconds;
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) throw new StudioInputError('Settings must be an object.');
  next.settings = { ...next.settings, ...patch } as StudioSettings;
  const sequence = next.settings.cinematicSequence;
  if (sequence && next.settings.durationSeconds !== oldDuration && !('cinematicSequence' in patch)) {
    const positions = next.positions;
    for (const item of sequence.waypoints) {
      const binding = positions[item.id];
      const resolved = resolveStudioPosition(points, next.settings, binding ?? { basis: 'film_percent', value: (item.filmProgress ?? 0) * 100 });
      item.filmProgress = resolved.filmProgress; item.anchor = resolved.anchor;
    }
    if (sequence.cameraTracks) for (const axis of CAMERA_TRACKS) for (const item of sequence.cameraTracks[axis] ?? []) {
      const binding: StudioPosition = positions[item.id ?? ''] ?? { basis: 'film_percent', value: item.seconds / oldDuration * 100 };
      item.seconds = resolveStudioPosition(points, next.settings, binding).compositionSeconds;
    }
  }
  if (sequence) cinematicTimeline(points, sequence, next.settings.durationSeconds);
  return StudioDocumentSchema.parse(next);
}

export { emptyCameraKeyframes };

export function retimeStudioDocument(document: StudioDocument, points: readonly RidePoint[], durationSeconds: number,
  policy: 'preserve_anchors' | 'scale' | 'preserve_seconds' | 'preserve_route' = 'preserve_anchors') {
  const next = structuredClone(document);
  if (policy !== 'preserve_anchors') for (const key of listStudioKeys(points, document.settings)) {
    if (!key.id) continue;
    next.positions[key.id] = policy === 'scale' ? { basis: 'film_percent', value: key.frame / (document.settings.durationSeconds * 30 - 1) * 100 }
      : policy === 'preserve_seconds' ? { basis: 'film_seconds', value: key.frame / 30 }
        : { basis: 'route_percent', value: resolveStudioPosition(points, document.settings, { basis: 'frame', value: key.frame }).routePercent };
  }
  return updateStudioSettings(next, points, { durationSeconds });
}
